import { useCallback } from 'react';
import type { ResourceType } from '@cotera/api';
import { useOptionalActionsContext } from '@cotera/client/v0/actions/framework';
import { ToggleFavoriteAction } from '../actions/toggle-favorite.action';
import { useFavorites } from './use-favorites';

/**
 * Flip a resource's favorite state — the one handler the star, the row menu and
 * the sidebar `×` share.
 *
 * Dispatches {@link ToggleFavoriteAction}, which is the only thing that writes a
 * favorite; the action owns the optimistic cache update and its rollback. Reads
 * the current state here only to hand the action the resolved direction. Off
 * shell (no actions provider — isolated tests) it is a no-op.
 */
export function useToggleFavorite(): (type: ResourceType, id: string) => void {
  const actions = useOptionalActionsContext();
  const { isFavorite } = useFavorites();
  return useCallback(
    (type: ResourceType, id: string) => {
      const action =
        actions !== null ? actions.getAction(ToggleFavoriteAction) : null;
      if (action === null || actions === null) {
        return;
      }
      void action.askOrExecute(
        { type, id, favorite: !isFavorite(type, id) },
        actions
      );
    },
    [actions, isFavorite]
  );
}
